import { api } from './api'

const apiVersion = 'v2'

export const webStoreOrderItemsApi = api
    .enhanceEndpoints({
        addTagTypes: ['OrderItems', 'OrderItemDrawing'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getOrderItems: builder.query({
                query: ({ orderId, organisationId }) =>
                    `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items`,
                providesTags: (result, _error, _arg) => [
                    { type: 'OrderItems', id: 'LIST' },
                    ...(result?.map(({ id }) => ({ type: 'OrderItems', id })) ?? []),
                ],
                transformResponse: (responseData) => {
                    if (responseData.length) {
                        responseData.sort((a, b) => a.index - b.index)
                    }
                    return responseData
                },
            }),

            addOrderItemsFromParts: builder.mutation({
                query: ({ orderId, organisationId, parts }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/add-items`,
                    method: 'POST',
                    body: parts,
                }),
                invalidatesTags: [{ type: 'OrderItems', id: 'LIST' }],
            }),

            addOrderItemsFromFiles: builder.mutation({
                query: ({ drawingFiles, orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/add-drawingfiles`,
                    method: 'POST',
                    body: drawingFiles,
                }),
                invalidatesTags: [{ type: 'OrderItems', id: 'LIST' }],
            }),

            addOrderItemsFrom3D: builder.mutation({
                query: ({ orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/add-3d-items`,
                    method: 'POST',
                }),
                invalidatesTags: [{ type: 'OrderItems', id: 'LIST' }],
            }),

            addOrderItemsFromPDF: builder.mutation({
                query: ({ orderId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/add-items-pdf`,
                    method: 'POST',
                }),
                invalidatesTags: [{ type: 'OrderItems', id: 'LIST' }],
            }),

            addOrderItemsFromPartLibrary: builder.mutation({
                query: ({ orderId, organisationId, partsToAdd }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/add-items-partlibrary`,
                    method: 'PUT',
                    body: partsToAdd,
                }),
                invalidatesTags: [{ type: 'OrderItems', id: 'LIST' }],
            }),

            updateOrderItem: builder.mutation({
                query: ({ orderId, orderItem, orderItemId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/${orderItemId}`,
                    method: 'PUT',
                    body: orderItem,
                }),
                invalidatesTags: [{ type: 'Order', id: 'LIST' }],
                async onQueryStarted(
                    { orderId, orderItem, orderItemId, organisationId },
                    { dispatch, queryFulfilled }
                ) {
                    dispatch(
                        webStoreOrderItemsApi.util.updateQueryData(
                            'getOrderItems',
                            { organisationId, orderId },
                            (draft) => {
                                const index = draft.findIndex((item) => item.id === orderItemId)
                                draft[index] = orderItem
                            }
                        )
                    )

                    try {
                        await queryFulfilled
                    } catch {
                        //patchResult.undo()
                        dispatch(webStoreOrderItemsApi.util.invalidateTags([{ type: 'OrderItems', id: orderItemId }]))
                    }
                },
            }),

            updateOrderItems: builder.mutation({
                query: ({ orderId, orderItems, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items`,
                    method: 'PUT',
                    body: orderItems,
                }),
                async onQueryStarted({ orderId, orderItems, organisationId }, { dispatch, queryFulfilled }) {
                    dispatch(
                        webStoreOrderItemsApi.util.updateQueryData(
                            'getOrderItems',
                            { organisationId, orderId },
                            (draft) => {
                                Object.assign(draft, orderItems)
                            }
                        )
                    )
                    try {
                        await queryFulfilled
                    } catch {
                        //patchResult.undo()
                        dispatch(webStoreOrderItemsApi.util.invalidateTags([{ type: 'OrderItems', id: 'LIST' }]))
                    }
                },
            }),

            updateOrderItemsAttributes: builder.mutation({
                query: ({ orderId, orderItems, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/update-attribute`,
                    method: 'PUT',
                    body: orderItems,
                }),
                invalidatesTags(result, error, arg) {
                    return arg.orderItems.quoteItemIds.map((quoteItemId) => ({
                        type: 'OrderItems',
                        id: quoteItemId,
                    }))
                },
            }),

            updateOrderItemDrawing: builder.mutation({
                query: ({ drawing, orderId, orderItemId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/${orderItemId}/update-drawing`,
                    method: 'PUT',
                    body: drawing,
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'OrderItems', id: arg.quoteItemId },
                    { type: 'OrderItemDrawing', id: 'DRAWING' },
                ],
            }),

            deleteOrderItem: builder.mutation({
                query: ({ orderId, orderItemId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/${orderItemId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (result, error, arg) => [
                    'WebStoreOrder',
                    { type: 'OrderItems', id: 'LIST' },
                    { type: 'OrderItems', id: arg.orderItemId },
                ],
            }),

            deleteOrderItems: builder.mutation({
                query: ({ orderId, orderItemsIds, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items`,
                    method: 'DELETE',
                    body: orderItemsIds,
                }),
                invalidatesTags: ['WebStoreOrder', 'OrderItems'],
            }),

            getOrderItemDrawing: builder.query({
                query: ({ orderId, orderItemId, organisationId }) =>
                    `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/${orderItemId}/get-drawing`,
                providesTags: (_result, _error, _arg) => [{ type: 'OrderItemDrawing', id: 'DRAWING' }],
            }),

            getOrderItemCadView: builder.query({
                query: ({ orderId, orderItemId, organisationId }) =>
                    `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/${orderItemId}/get-cadview`,
            }),

            resetOrderItemDrawing: builder.mutation({
                query: ({ orderId, orderItemId, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/${orderItemId}/reset-toolpath`,
                    method: 'PUT',
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'OrderItems', id: arg.orderItemId },
                    { type: 'OrderItemDrawing', id: 'DRAWING' },
                ],
            }),

            scaleOrderItemDrawing: builder.mutation({
                query: ({ orderId, orderItemId, organisationId, scaleFactor }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}/quote-items/${orderItemId}/scale-drawing`,
                    method: 'PUT',
                    params: { scaleFactor },
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'OrderItems', id: arg.orderItemId },
                    { type: 'OrderItemDrawing', id: 'DRAWING' },
                ],
            }),
        }),
    })

export const selectOrderItems = (state, query) => {
    return webStoreOrderItemsApi.endpoints.getOrderItems.select(query)(state)?.data ?? []
}

export const selectOrderItemsForOrder = (state, query) => {
    const orderItems = selectOrderItems(state, query)
    return orderItems.map(({ id, index, name, orderId }) => ({ id, name, index, orderId }))
}

export const {
    useAddOrderItemsFrom3DMutation,
    useAddOrderItemsFromFilesMutation,
    useAddOrderItemsFromPDFMutation,
    useAddOrderItemsFromPartLibraryMutation,
    useAddOrderItemsFromPartsMutation,
    useDeleteOrderItemMutation,
    useDeleteOrderItemsMutation,
    useGetOrderItemCadViewQuery,
    useGetOrderItemDrawingQuery,
    useGetOrderItemsQuery,
    useLazyGetOrderItemDrawingQuery,
    useLazyGetOrderItemsQuery,
    useResetOrderItemDrawingMutation,
    useScaleOrderItemDrawingMutation,
    useUpdateOrderItemDrawingMutation,
    useUpdateOrderItemMutation,
    useUpdateOrderItemsAttributesMutation,
    useUpdateOrderItemsMutation,
} = webStoreOrderItemsApi
